import React, { useContext } from 'react';
import Context, { useAppContext } from '../../contexts/MarketPerformanceContext';
import MarketGroupsNav from './MarketGroupsNav';
import MarketGroupsError from './MarketGroupsError';
import MarketIndex from './MarketIndex';
import InstrumentDetails from './InstrumentDetails';
import InstrumentPerformance from './InstrumentPerformance';
import RisesAndFallsTable from './RisesAndFallsTable';
import FullIndices from './FullIndices';
import { LottieLoader } from '../../utilities/Loaders';
import { VoidState } from '../../utilities/VoidState';
import FullInstrumentsView from './Instruments/FullInstrumentsView';
import { marketsView } from '../../../primary/components/analytics/events/markets';
import { setupAnalytics } from '../../../primary/components/analytics/default';
import { MarketClosedMessage } from '../MarketPerformance/MarketTime';

const analytics = setupAnalytics();

const MarketGroups = () => {
    const context = useContext(Context);

    return (
        <div className="market-groups-app">
            <div className="section fb-pt-7 pb-4">
                <div className="container">
                    <div className="market-groups-title">
                        <h2 className="h2">{context.state.header.text}</h2>
                    </div>
                    <MarketGroupsNav/>
                </div>
            </div>
            <View/>
        </div>
    );
};

const View = () => {
    const context = useContext(Context);

    switch (context.state.currentView) {
    case 'InstrumentDetails':
        return <InstrumentDetailsView/>;
    case 'FullIndices':
        return <FullIndicesView/>;
    case 'FullInstruments':
        return <FullInstrumentsView />;
    default:
        return <DefaultView/>;
    }
};

const DefaultView = () => {
    const context = useContext(Context);

    if (!context.state.loading && context.state.showError) {
        return <MarketGroupsError/>;
    }

    return (
        <div className="index-view">
            <DefaultComponents/>
        </div>
    );
};

const DefaultComponents = () => {
    const context = useContext(Context);
    const { state: { marketIndex, currentTabId, instrument } } = useAppContext();

    if (context.state.loading) {
        return (
            <div className='section'>
                <div className='container d-flex'>
                    <div className="mx-auto align-self-center d-inline-block h5 font-weight-bold">
                        <LottieLoader/>
                    </div>
                </div>
            </div>
        );
    }

    if (
        (!marketIndex.indices || !marketIndex.indices.length)
        || ((!marketIndex.risesAndFalls || !marketIndex.risesAndFalls.length !== 0) && (!marketIndex.asAt || !marketIndex.asAt.length))
        || ((!marketIndex.highlights || !marketIndex.highlights.length === 0) && (!marketIndex.asAt || !marketIndex.asAt.length))
        || ((!marketIndex.instruments || !marketIndex.instruments.length != 0) && (!marketIndex.asAt || !marketIndex.asAt.length))
    ) {
        return (
            <VoidState message='Unable to load market data' classes='market-index'/>
        );
    }

    const isFullInstrumentsView = context.state.currentView === 'FullInstruments';

    const displayOtherSecurities = () => {
        context.getFullInstruments(context.state.currentTabId);
        const marketPerformance = document.getElementById('marketPerformance');

        if (marketPerformance) {
            window.scrollTo({ top: marketPerformance.offsetTop, behavior: 'smooth' });
        }
    };

    return (
        <>
            <div className={`section ${isFullInstrumentsView ? 'd-none' : ''}`}>
                <div className="container">
                    {marketIndex?.openingHours?.isOpen ? (
                        <div className={'font-weight-bold pt-3 pb-4'}>
                            {instrument?.marketGroup} Today
                        </div>
                    ) : (
                        <div className={'pt-3 pb-4'}>
                            <MarketClosedMessage
                                marketGroup={instrument?.marketGroup}
                                openingLabel={marketIndex?.openingHours?.marketOpeningLabel}
                            />
                        </div>
                    )}
                    <div className="instrument-performance">
                        <InstrumentPerformance />
                    </div>
                </div>
            </div>
            <div className={`section grey-bg fb-pt-7 fb-pb-7 ${isFullInstrumentsView ? 'd-none' : ''}`}>
                <div className="container">
                    <div className="index">
                        <MarketIndex/>
                    </div>
                </div>
            </div>
            <div className={`section ${isFullInstrumentsView ? 'd-none' : ''}`}>
                <div className="container">
                    <div className="changes">
                        <RisesAndFallsTable />
                        <div className="outbound clearfix mt-4">
                            <button
                                type="button"
                                className='outbound-link pull-right other-securities'
                                onClick={() => {
                                    displayOtherSecurities();
                                    if (currentTabId != null) {
                                        analytics.trackEvent(marketsView({ title: `View Other ${currentTabId?.toUpperCase()} Securities` }));
                                    }
                                }}>
                                Other Securities <span className="icon chevron-blue chevron-right" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const InstrumentDetailsView = () => {
    const context = useContext(Context);

    if (!context.state.loading && context.state.showError) {
        return <MarketGroupsError/>;
    }

    return (
        <div className="section">
            <div className="container">
                <div className="nzdx-instruments">
                    {context.state.loading ? (
                        <div className="text-center pb-4 h5 font-weight-bold text-uppercase">
                            <LottieLoader/>
                        </div>
                    ) : (
                        <InstrumentDetails/>
                    )}
                </div>
            </div>
        </div>
    );
};

const FullIndicesView = () => {
    const context = useContext(Context);

    return (
        <div className="section">
            <div className="container">
                <div className="instrument-performance">
                    {context.state.loading ? (
                        <div className="text-center pb-4 h5 font-weight-bold text-uppercase">
                            <LottieLoader/>
                        </div>
                    ) : (
                        <FullIndices/>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MarketGroups;
